import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import CheckIcon from '../icons/CheckIcon'

interface ToastProps {
  message: string
  visible: boolean
  icon?: 'check' | 'none'
  onClose: () => void
  duration?: number
}

function Toast({
  message,
  visible,
  icon = 'check',
  onClose,
  duration = 5000,
}: ToastProps) {
  const [stage, setStage] = useState<'enter' | 'visible' | 'exit' | 'hidden'>(
    visible ? 'visible' : 'hidden'
  )

  useEffect(() => {
    if (visible) {
      setStage('enter')
      const enterTimer = setTimeout(() => setStage('visible'), 20)
      const exitTimer = setTimeout(() => setStage('exit'), duration)
      const hideTimer = setTimeout(() => {
        setStage('hidden')
        onClose()
      }, duration + 300)
      return () => {
        clearTimeout(enterTimer)
        clearTimeout(exitTimer)
        clearTimeout(hideTimer)
      }
    } else {
      setStage('hidden')
    }
  }, [visible, duration, onClose])

  if (stage === 'hidden') return null

  return (
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <output
      role="status"
      aria-live="polite"
      className={clsx(
        'fixed bottom-[73px] left-1/2 -translate-x-1/2 z-50',
        'bg-gray-0 text-white rounded-[4px] p-3',
        'text-[14px] font-medium leading-[1.2] tracking-[0.0025em]',
        'flex items-center gap-3',
        'transition-all duration-300 ease-out',
        stage === 'enter' && 'translate-y-4 opacity-0',
        stage === 'visible' && 'translate-y-0 opacity-100',
        stage === 'exit' && 'translate-y-4 opacity-0'
      )}
    >
      <span className="min-h-[20px] flex items-center">{message}</span>
      {icon === 'check' && <CheckIcon height="h-5" width="w-5" />}
    </output>
  )
}

export default Toast
